import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { TIER_THEMES } from "@/lib/themes";

export default function ProjectDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [assocs, setAssocs] = useState([]);
  const [form, setForm] = useState({
    name: "", role: "lighting", email: "", phone: "",
    association_id: "", project_association_override: "", daily_rate: 0,
  });

  const load = async () => {
    const { data } = await api.get(`/projects/${id}`);
    setData(data);
  };
  useEffect(() => {
    load();
    api.get("/meta/roles").then((r) => setRoles(r.data));
    api.get("/meta/associations").then((r) => setAssocs(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addCrew = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${id}/crew`, form);
      toast.success("Crew added");
      setForm({ ...form, name: "", email: "", phone: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    }
  };

  const remove = async (crewId) => {
    await api.delete(`/projects/${id}/crew/${crewId}`);
    toast.success("Removed");
    load();
  };

  if (!data) return <div className="p-10 text-white/40 uppercase tracking-[0.3em]">Loading…</div>;

  const { project, crew, crew_count } = data;
  const cap = project.crew_cap;
  const pct = Math.round((crew_count / cap) * 100);
  const nearCap = pct >= 80;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="project-detail-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Project</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">{project.title}</h1>
        <div className="mt-2 text-sm text-white/55">{project.description || "—"} · {project.location || "Location TBD"}</div>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <span className={`text-[10px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full ${
            project.status === "wrapped" ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/70 border border-white/10"
          }`}>
            {project.status === "wrapped" ? "✓ Wrapped" : (project.status || "").replace("_"," ")}
          </span>
          {project.status !== "wrapped" && (
            <Button
              onClick={async () => {
                if (!window.confirm(`Mark "${project.title}" as WRAPPED? This adds a verified credit to every linked crew member's profile.`)) return;
                try {
                  const { data } = await api.post(`/projects/${project.id}/wrap`);
                  toast.success(`Wrapped! ${data.credits_added} verified credit(s) added.`);
                  load();
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail));
                }
              }}
              className="btn-themed h-8 px-4 rounded-full uppercase tracking-[0.2em] text-[10px]"
              data-testid="wrap-project-btn">
              Mark Wrapped · Verify Credits
            </Button>
          )}
        </div>
      </div>

      {/* Cap bar */}
      <div className="cs-card rounded-2xl p-6 mb-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">Crew Cap · Plan</div>
            <div className="font-display text-lg uppercase tracking-tight">{project.plan?.replace("_", " ")}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50">Used</div>
            <div className="font-display text-2xl tabular-nums" data-testid="crew-count">{crew_count} / {cap}</div>
          </div>
        </div>
        <Progress value={pct} className="h-2 bg-white/5" />
        {nearCap && (
          <div className="mt-3 text-xs uppercase tracking-[0.2em] text-[#F5B82E]" data-testid="cap-warning">
            ⚠ Near cap. Upgrade plan to expand crew beyond {cap}.
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Add crew form */}
        <form onSubmit={addCrew} className="cs-card rounded-2xl p-6 space-y-4 h-fit" data-testid="add-crew-form">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Add</div>
            <div className="font-display text-xl uppercase tracking-tight">New Crew Member</div>
          </div>
          <Field label="Name">
            <Input value={form.name} required data-testid="crew-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="bg-black/40 border-white/15" data-testid="crew-role"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label} · {r.tier}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} data-testid="crew-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Primary Association">
            <Select value={form.association_id || "none"} onValueChange={(v) => setForm({ ...form, association_id: v === "none" ? "" : v })}>
              <SelectTrigger className="bg-black/40 border-white/15" data-testid="crew-association"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {assocs.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Project Association Override (interstate/inter-union)">
            <Select value={form.project_association_override || "none"}
              onValueChange={(v) => setForm({ ...form, project_association_override: v === "none" ? "" : v })}>
              <SelectTrigger className="bg-black/40 border-white/15" data-testid="crew-override"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {assocs.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Daily Rate (INR)">
            <Input type="number" value={form.daily_rate} data-testid="crew-rate"
              onChange={(e) => setForm({ ...form, daily_rate: parseFloat(e.target.value) || 0 })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Button type="submit" className="btn-themed w-full rounded-full uppercase tracking-[0.25em] text-xs h-11" data-testid="crew-submit">
            <UserPlus size={14} className="mr-2" /> Add to crew
          </Button>
        </form>

        {/* Crew list */}
        <div className="lg:col-span-2 cs-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Roster</div>
              <div className="font-display text-xl uppercase tracking-tight">Crew Members</div>
            </div>
          </div>
          {crew.length === 0 ? (
            <div className="text-center py-10 text-white/40 text-sm">No crew added yet.</div>
          ) : (
            <div className="space-y-3" data-testid="crew-list">
              {crew.map((c) => {
                const tier = TIER_THEMES[c.tier];
                return (
                  <div key={c.id} className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0">
                    <div className="w-2 h-10 rounded" style={{ background: tier?.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-xs text-white/55">{c.role.replace("_", " ")} · {tier?.label}</div>
                    </div>
                    <div className="text-xs text-white/50 hidden sm:block">
                      {c.project_association_override
                        ? `Override → ${c.project_association_override.toUpperCase()}`
                        : (c.association_id || "—").toUpperCase()}
                    </div>
                    <button onClick={() => remove(c.id)} className="p-2 text-white/40 hover:text-red-400 transition"
                            data-testid={`remove-crew-${c.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
